import DesktopPageHeader, {
  MenuPageHeader,
} from '@pinecast/common/DesktopPageHeader';
import {useIsMobile} from '@pinecast/common/hooks/useIsMobile';
import type {IntlShape} from '@pinecast/i18n';
import {useIntl} from '@pinecast/i18n';
import {focusVisibleRing, useColors, useCSS} from '@pinecast/styles';
import * as React from 'react';
import {Link, matchPath, useLocation, useMatch} from 'react-router';

import NavDrawer, {
  NavDrawerButton,
  NavDrawerList,
  navDrawerItemStyles,
} from '../NavDrawer';

import {MainContent} from './MainContent';
import PageNotFound from './PageNotFound';
import {PageTitleScope, usePageSubtitle} from './usePageTitle';

export type ChromeSidebarLink = {
  children?: React.ReactNode;
  exact?: boolean;
  /**
   * A page that has no link here: one that the account cannot use, or one
   * whose link waits for data that is still loading. It still names the page
   * in the document title.
   */
  hidden?: boolean;
  path: string;
  labelMessage: (intl: IntlShape) => string;
  /**
   * The heading of the page that the link goes to, when it is not the link's
   * label ("Podcast overview" for "Overview"). The sidebar renders it above the
   * page, so the page has no heading of its own, and the heading does not
   * depend on the page having loaded. On a phone it is also the button that
   * opens the drawer.
   */
  headingMessage?: (intl: IntlShape) => string;
};

type Props = {
  children: React.ReactNode;
  /**
   * The links, shown and hidden, are all the pages of the dashboard. At an
   * address that none of them matches, the sidebar shows `PageNotFound` in
   * place of `children`, also while the dashboard loads. The account
   * dashboard leaves it off, because the Studio has pages under its link.
   */
  exhaustive?: boolean;
  links: Array<ChromeSidebarLink | null | false | undefined>;
  /**
   * The name of the links, such as "Podcast menu": of the `<nav>` that holds
   * them on a wider screen, and of the drawer that holds them on a phone.
   * Pages outside the main dashboards fall back to "Menu".
   */
  menuLabel?: (intl: IntlShape) => string;
};

/**
 * The links between the pages of a dashboard, and the `<main>` of the page
 * beside them. The current link names the page in the document title, inside
 * the title of the dashboard around it ("Analytics - My Podcast - Pinecast"),
 * unless the page sets a title of its own.
 *
 * It also renders the heading of the page, at the top of the `<main>`, from the
 * current link. On a phone that heading is the button that opens the drawer of
 * links. Because it does not come from the page, it is there, and the same,
 * while the page loads or fails to load.
 */
const ChromeSidebar = ({children, exhaustive, links, menuLabel}: Props) => {
  const css = useCSS();
  const isMobile = useIsMobile();

  const intl = useIntl();

  const location = useLocation();

  const pages = links.filter((link): link is ChromeSidebarLink => !!link);
  const shownLinks = pages.filter(link => !link.hidden);
  // The current page, matched as the links match, with a link or without.
  const currentLink = pages.find(link =>
    matchPath({path: link.path, end: link.exact ?? true}, location.pathname),
  );
  usePageSubtitle(currentLink ? currentLink.labelMessage(intl) : null);

  const label = menuLabel
    ? menuLabel(intl)
    : intl.formatMessage({
        description:
          'Title of the drawer that holds the navigation links of a dashboard page on a narrow screen, and of the button that opens it',
        defaultMessage: 'Menu',
      });
  const phone = usePhoneMenu(label);

  // `data-focus-fallback`: where focus goes when the element that had it is
  // gone and nothing nearer takes it, as when the last row of a list is
  // deleted.
  const heading =
    currentLink &&
    (isMobile ? (
      <MenuPageHeader data-focus-fallback menu={phone.menu}>
        {(currentLink.headingMessage ?? currentLink.labelMessage)(intl)}
      </MenuPageHeader>
    ) : (
      <DesktopPageHeader data-focus-fallback>
        {(currentLink.headingMessage ?? currentLink.labelMessage)(intl)}
      </DesktopPageHeader>
    ));

  return (
    <div
      className={css({
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
      })}
    >
      {isMobile ? (
        // Only where no link names the address: with no heading to hold the
        // button, it is its own.
        !currentLink && (
          <NavDrawerButton
            expanded={phone.menu.open}
            label={label}
            onClick={phone.menu.onOpen}
            style={{marginBottom: '20px'}}
          />
        )
      ) : (
        <nav
          aria-label={label}
          className={css({
            display: 'flex',
            flex: '0 0 160px',
            flexDirection: 'column',
            // Room for a link's focus ring between it and the next link. The
            // ring reaches 4px out, and the current link's fill is the ring's
            // own color in light mode, so a ring that ran into it would
            // disappear on that side.
            gap: '6px',
            marginRight: '20px',
            paddingTop: '40px', // Page heading height plus margin
          })}
        >
          {shownLinks.map(link => (
            <ChromeSidebarItem key={link.path} {...link} />
          ))}
        </nav>
      )}
      {/* In the same place at every width, so that crossing the breakpoint
          does not mount the page again. */}
      <MainContent style={{flex: '1 1', maxWidth: '1024px'}}>
        <PageTitleScope>
          {heading}
          {currentLink || !exhaustive ? children : <PageNotFound />}
        </PageTitleScope>
      </MainContent>
      {isMobile && phone.menu.open && (
        <NavDrawer label={label} onClose={phone.close}>
          {() => (
            <NavDrawerList>
              {shownLinks.map(link => (
                <ChromeSidebarMenuItem
                  key={link.path}
                  {...link}
                  onChoose={phone.close}
                />
              ))}
            </NavDrawerList>
          )}
        </NavDrawer>
      )}
    </div>
  );
};

/**
 * The drawer of links on a phone: whether it is open, and how to open and close
 * it. The page's heading is the button that opens it, and it is the same
 * element on every page, so when the drawer closes, after a link is chosen too,
 * focus goes back to it (`NavDrawer`).
 */
const usePhoneMenu = (label: string) => {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const onOpen = React.useCallback(() => setOpen(true), []);
  const close = React.useCallback(() => setOpen(false), []);
  const menu = React.useMemo(
    () => ({label, onOpen, open}),
    [label, onOpen, open],
  );

  // Back and forward change the page without the drawer, which then closes.
  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return {close, menu};
};

export default ChromeSidebar;

const ChromeSidebarItem = ({
  children,
  exact = true,
  labelMessage,
  path,
}: ChromeSidebarLink) => {
  const matches = !!useMatch({path: path, end: exact});
  const css = useCSS();
  const designColors = useColors();
  const intl = useIntl();
  return (
    <Link
      aria-current={matches ? 'page' : undefined}
      className={css({
        alignSelf: 'flex-start',
        borderBottomRightRadius: '4px',
        borderTopRightRadius: '4px',
        borderBottomLeftRadius: '4px',
        borderTopLeftRadius: '4px',
        color: designColors.contentText,
        display: 'inline-block',
        fontWeight: 400,
        lineHeight: '16px',
        paddingBottom: '8px',
        paddingLeft: '12px',
        paddingTop: '8px',
        paddingRight: '12px',
        textDecoration: 'none',
        whiteSpace: 'nowrap',
        ...(matches && {
          backgroundColor: designColors.accentBlue,
          color: designColors.accentBlueText,
          fontWeight: 500,
        }),

        // Around the pill, with a gap, so that the ring shows on the page and
        // not on the current link's fill, which is the ring's own color in
        // light mode. The link is the pill: a ring on a link as wide as the
        // sidebar would stand well clear of the text.
        ...focusVisibleRing(designColors),
      })}
      to={path}
    >
      {children || labelMessage(intl)}
    </Link>
  );
};

const ChromeSidebarMenuItem = ({
  children,
  exact = true,
  labelMessage,
  onChoose,
  path,
}: ChromeSidebarLink & {onChoose: () => void}) => {
  const current = !!useMatch({path: path, end: exact});
  const css = useCSS();
  const designColors = useColors();
  const intl = useIntl();
  return (
    <li>
      <Link
        aria-current={current ? 'page' : undefined}
        className={css(navDrawerItemStyles(current, designColors))}
        onClick={onChoose}
        to={path}
      >
        {children || labelMessage(intl)}
      </Link>
    </li>
  );
};
